import React, { memo } from 'react';
import "./styles.css";
import { useLocation, useNavigate } from 'react-router-dom'; // useLocation import qilindi
import { ArrowLeft, BookOpen, ExternalLink, Mail, Phone, UserX } from 'lucide-react';
import { Badge, Button, Card, EmptyState } from '../ui';
import { mediaUrl } from '../../utils/media';
import { formatServerDateTime } from '../../utils/serverDate';
import { EMPTY, eduLevelTitle, isFilled } from '../Students/studentProfile';
import "./StudentInfo.css";

// Ko'rinish uchun maydonlar guruhi (ma'lumot manbai avvalgidek: location.state.user).
const FIELD_GROUPS = [
    {
        title: "Shaxsiy ma'lumotlar",
        fields: [
            { id: 'first_name', label: 'Ism' },
            { id: 'last_name', label: 'Familiya' },
            { id: 'middle_name', label: 'Otasining ismi' },
            { id: 'birthdate', label: "Tug'ilgan sana", type: 'date' },
            { id: 'gender', label: 'Jinsi' },
            { id: 'citizenship', label: 'Fuqarolik' },
            { id: 'edu_level', label: "Ta'lim darajasi", type: 'eduLevel' },
        ],
    },
    {
        title: 'Aloqa',
        fields: [
            { id: 'phone_number', label: 'Telefon' },
            { id: 'email', label: 'Email' },
        ],
    },
    {
        title: 'Manzil',
        fields: [
            { id: 'living_country', label: 'Davlat' },
            { id: 'living_address', label: 'Viloyat' },
            { id: 'living_city', label: 'Shahar' },
            { id: 'postal_code', label: 'Pochta indeksi' },
        ],
    },
    {
        title: 'Hujjatlar va imtihon',
        fields: [
            { id: 'identity_card', label: 'Pasport', type: 'document' },
            { id: 'edu_doc', label: "Ta'lim hujjati", type: 'document' },
            { id: 'exam_language', label: 'Imtihon tili' },
        ],
    },
];

// "2001-05-14" → "14.05.2001"
const formatDay = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split('-').reverse().join('.') : String(value));

// Bo'sh qiymat (null, "") → "—". Hujjat: nisbiy yo'lga base URL qo'shiladi; bo'sh bo'lsa havola ko'rsatilmaydi.
const InfoValue = ({ value, type }) => {
    const shown = type === 'eduLevel' ? eduLevelTitle(value) : value;
    if (!isFilled(shown)) {
        return <span className="ui-student-info__empty" title="Kiritilmagan">{EMPTY}</span>;
    }
    if (type === 'document') {
        return (
            <a href={mediaUrl(shown)} target="_blank" rel="noopener noreferrer" className="ui-student-info__link">
                Ko'rish <ExternalLink aria-hidden="true" />
            </a>
        );
    }
    if (type === 'date') return <span className="ui-student-info__text">{formatDay(shown)}</span>;
    return <span className="ui-student-info__text">{String(shown)}</span>;
};

const getPhotoSrc = (user) => mediaUrl(user.photo || user.avatar || user.image);

const RequestReview = memo(() => {
    const location = useLocation(); // location obyektini olish
    const user = location.state?.user || {}; // location.state ichidan user ma'lumotlarini olish
    const navigate = useNavigate();

    const hasUser = Boolean(location.state?.user);
    const fullName = [user.first_name, user.last_name, user.middle_name].filter(Boolean).join(' ');
    const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
    const photoSrc = getPhotoSrc(user);

    return (
        <div className="ui-student-info">
            <div>
                <Button variant="ghost" size="sm" leftIcon={<ArrowLeft />} onClick={() => navigate(-1)}>
                    Orqaga
                </Button>
            </div>

            {!hasUser ? (
                <Card padding="none">
                    <EmptyState
                        icon={<UserX />}
                        title="Student ma'lumoti topilmadi"
                        description="Studentlar ro'yxatiga qaytib, qaytadan oching"
                        action={
                            <Button size="sm" onClick={() => navigate('/students')}>
                                Studentlar ro'yxati
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <>
                    <Card>
                        <div className="ui-student-info__profile">
                            {photoSrc ? (
                                <img src={photoSrc} alt="" className="ui-student-info__avatar" loading="lazy" />
                            ) : (
                                <span className="ui-student-info__avatar ui-student-info__avatar--initials" aria-hidden="true">
                                    {initials || '?'}
                                </span>
                            )}
                            <div className="ui-student-info__identity">
                                <div className="ui-student-info__name-row">
                                    <h1 className="ui-student-info__name">{fullName || 'Ismsiz student'}</h1>
                                    <Badge variant="brand" size="sm">Student</Badge>
                                </div>
                                <div className="ui-student-info__contacts">
                                    {user.email && (
                                        <span className="ui-student-info__contact">
                                            <Mail aria-hidden="true" /> {user.email}
                                        </span>
                                    )}
                                    {user.phone_number && (
                                        <span className="ui-student-info__contact">
                                            <Phone aria-hidden="true" /> {user.phone_number}
                                        </span>
                                    )}
                                </div>
                                {formatServerDateTime(user.created_at) && (
                                    <span className="ui-student-info__registered">
                                        Ro'yxatdan o'tgan: {formatServerDateTime(user.created_at)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </Card>

                    {FIELD_GROUPS.map((group) => (
                        <Card key={group.title} title={group.title}>
                            <dl className="ui-student-info__grid">
                                {group.fields.map(({ id, label, type }) => (
                                    <div key={id} className="ui-student-info__item">
                                        <dt className="ui-student-info__label">{label}</dt>
                                        <dd className="ui-student-info__value">
                                            <InfoValue value={user[id]} type={type} />
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </Card>
                    ))}

                    {/* Kurslar — faqat javobda courses bo'lsa (studentlar ro'yxatidan ochilganda) */}
                    {Array.isArray(user.courses) && (
                        <Card title="Kurslar">
                            {user.courses.length ? (
                                <ul className="ui-student-info__courses">
                                    {user.courses.map((course) => (
                                        <li key={course.id} className="ui-student-info__course">
                                            <span className="ui-student-info__course-title">{course.title || EMPTY}</span>
                                            <span className="ui-student-info__course-meta">
                                                Progress: {course.progress ?? 0}%
                                                {course.status === 'ACTIVE' ? (
                                                    <Badge variant="success" size="sm">Faol</Badge>
                                                ) : course.status ? (
                                                    <Badge variant="neutral" size="sm">{course.status}</Badge>
                                                ) : null}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyState
                                    icon={<BookOpen />}
                                    title="Hali kursga yozilmagan"
                                    description="To'lov yoki promokod muvaffaqiyatli o'tgach, kurslar shu yerda ko'rinadi."
                                />
                            )}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
});

RequestReview.displayName = 'RequestReview';

export default RequestReview;
